import React, { useState, useEffect, useRef } from 'react';
import { SectionComment, AiSuggestion, SectionCommentData, SectionAttachment } from '../types';
import apiClient from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

interface SectionCommentPopupProps {
  questionnaireId: string;
  category: string;
  questions: string[];
  onClose: () => void;
  onCommentCountChange: (count: number) => void;
}

type TabType = 'comments' | 'notes' | 'ai';

const SectionCommentPopup: React.FC<SectionCommentPopupProps> = ({
  questionnaireId,
  category,
  questions,
  onClose,
  onCommentCountChange,
}) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<TabType>('comments');
  const [data, setData] = useState<SectionCommentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [aiGenerating, setAiGenerating] = useState(false);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [formText, setFormText] = useState('');
  const [formType, setFormType] = useState<'comment' | 'note' | 'suggestion'>('comment');
  const [formPriority, setFormPriority] = useState<string>('');
  const [formTags, setFormTags] = useState('');
  const [formAttachments, setFormAttachments] = useState<SectionAttachment[]>([]);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlValue, setUrlValue] = useState('');
  const [urlTitle, setUrlTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadComments();
  }, [questionnaireId, category]);

  const loadComments = async () => {
    try {
      setLoading(true);
      const result = await apiClient.getSectionComments(questionnaireId, category);
      setData(result);
      onCommentCountChange(result?.comments?.length || 0);
    } catch (err) {
      console.error('Failed to load section comments:', err);
      setData({ questionnaireId, category, comments: [], aiSuggestions: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!formText.trim()) return;
    try {
      const comment = await apiClient.addSectionComment(questionnaireId, category, {
        text: formText,
        type: formType,
        author: 'user',
        priority: formPriority,
        tags: formTags ? formTags.split(',').map(t => t.trim()).filter(Boolean) : [],
        attachments: formAttachments,
      });
      setData(prev => prev ? { ...prev, comments: [...prev.comments, comment] } : prev);
      onCommentCountChange((data?.comments.length || 0) + 1);
      resetForm();
    } catch (err) {
      console.error('Failed to add comment:', err);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await apiClient.deleteSectionComment(questionnaireId, category, commentId);
      setData(prev => {
        if (!prev) return prev;
        const updated = { ...prev, comments: prev.comments.filter(c => c.commentId !== commentId) };
        onCommentCountChange(updated.comments.length);
        return updated;
      });
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const handleGenerateAi = async () => {
    setAiGenerating(true);
    try {
      const suggestions = await apiClient.generateAiSuggestions(questionnaireId, category, questions);
      setData(prev => prev ? { ...prev, aiSuggestions: suggestions } : prev);
    } catch (err) {
      console.error('Failed to generate AI suggestions:', err);
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAcceptSuggestion = async (suggestion: AiSuggestion) => {
    try {
      const comment = await apiClient.addSectionComment(questionnaireId, category, {
        text: suggestion.text,
        type: 'suggestion',
        author: 'AI',
        priority: '',
        tags: [suggestion.type],
        attachments: [],
      });
      setData(prev => {
        if (!prev) return prev;
        const updatedSuggestions = prev.aiSuggestions.map(s =>
          s.suggestionId === suggestion.suggestionId ? { ...s, accepted: true } : s
        );
        return { ...prev, comments: [...prev.comments, { ...comment, aiGenerated: true }], aiSuggestions: updatedSuggestions };
      });
      onCommentCountChange((data?.comments.length || 0) + 1);
    } catch (err) {
      console.error('Failed to accept suggestion:', err);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await apiClient.uploadCommentImage(file);
      setFormAttachments(prev => [...prev, {
        type: 'image' as const,
        url: result.url,
        name: file.name,
      }]);
    } catch (err) {
      console.error('Failed to upload image:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAddUrl = () => {
    if (!urlValue.trim()) return;
    setFormAttachments(prev => [...prev, {
      type: 'url' as const,
      url: urlValue.trim(),
      name: urlTitle.trim() || urlValue.trim(),
      title: urlTitle.trim() || undefined,
    }]);
    setUrlValue('');
    setUrlTitle('');
    setShowUrlInput(false);
  };

  const removeAttachment = (idx: number) => {
    setFormAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const resetForm = () => {
    setFormText('');
    setFormType('comment');
    setFormPriority('');
    setFormTags('');
    setFormAttachments([]);
    setShowForm(false);
    setShowUrlInput(false);
    setUrlValue('');
    setUrlTitle('');
  };

  const getFilteredComments = (): SectionComment[] => {
    if (!data) return [];
    if (activeTab === 'comments') return data.comments.filter(c => c.type === 'comment' || c.type === 'suggestion');
    if (activeTab === 'notes') return data.comments.filter(c => c.type === 'note');
    return [];
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#dc3545';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getSuggestionTypeColor = (type: string) => {
    switch (type) {
      case 'recommendation': return '#007bff';
      case 'observation': return '#17a2b8';
      case 'risk': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getSuggestionTypeLabel = (type: string) => {
    switch (type) {
      case 'recommendation': return t('sectionComments', 'recommendation');
      case 'observation': return t('sectionComments', 'observation');
      case 'risk': return t('sectionComments', 'risk');
      default: return type;
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '700px',
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e9ecef',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px' }}>{t('sectionComments', 'title')}</h3>
            <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '4px' }}>{category}</div>
          </div>
          <button onClick={onClose} style={{
            background: 'none',
            border: 'none',
            fontSize: '20px',
            cursor: 'pointer',
            color: '#6c757d',
            padding: '4px 8px',
          }}>&times;</button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e9ecef',
          padding: '0 20px',
        }}>
          {(['comments', 'notes', 'ai'] as TabType[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '10px 16px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === tab ? 600 : 400,
                color: activeTab === tab ? '#007bff' : '#6c757d',
                borderBottom: activeTab === tab ? '2px solid #007bff' : '2px solid transparent',
                marginBottom: '-1px',
              }}
            >
              {tab === 'comments' && t('sectionComments', 'tabComments')}
              {tab === 'notes' && t('sectionComments', 'tabNotes')}
              {tab === 'ai' && t('sectionComments', 'tabAi')}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#6c757d' }}>Loading...</div>
          ) : (
            <>
              {/* Comments / Notes Tab */}
              {(activeTab === 'comments' || activeTab === 'notes') && (
                <>
                  {getFilteredComments().length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px', color: '#6c757d', fontSize: '14px' }}>
                      {activeTab === 'comments' ? t('sectionComments', 'noComments') : t('sectionComments', 'noNotes')}
                    </div>
                  ) : (
                    <div>
                      {getFilteredComments().map(comment => (
                        <div key={comment.commentId} style={{
                          padding: '12px',
                          marginBottom: '10px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          border: '1px solid #e9ecef',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '14px', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                                {comment.text}
                              </div>
                              {/* Attachments */}
                              {comment.attachments && comment.attachments.length > 0 && (
                                <div style={{ marginTop: '8px' }}>
                                  {comment.attachments.map((att, idx) => (
                                    <div key={idx} style={{ marginBottom: '4px' }}>
                                      {att.type === 'image' ? (
                                        <img src={window.location.protocol === 'https:' ? att.url : `http://${window.location.hostname}:3010${att.url}`} alt={att.name} style={{ maxWidth: '200px', maxHeight: '120px', borderRadius: '4px' }} />
                                      ) : att.type === 'url' ? (
                                        <a href={att.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: '#007bff' }}>
                                          {att.title || att.name || att.url}
                                        </a>
                                      ) : null}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {/* Tags & Priority */}
                              <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                {comment.priority && (
                                  <span style={{
                                    fontSize: '11px',
                                    padding: '2px 6px',
                                    borderRadius: '10px',
                                    backgroundColor: getPriorityColor(comment.priority),
                                    color: '#fff',
                                  }}>
                                    {comment.priority}
                                  </span>
                                )}
                                {comment.aiGenerated && (
                                  <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '10px', backgroundColor: '#6f42c1', color: '#fff' }}>
                                    {t('sectionComments', 'aiGenerated')}
                                  </span>
                                )}
                                {comment.tags?.map((tag, tIdx) => (
                                  <span key={tIdx} style={{
                                    fontSize: '11px',
                                    padding: '2px 6px',
                                    borderRadius: '10px',
                                    backgroundColor: '#e9ecef',
                                    color: '#495057',
                                  }}>
                                    {tag}
                                  </span>
                                ))}
                                <span style={{ fontSize: '11px', color: '#adb5bd', marginLeft: 'auto' }}>
                                  {new Date(comment.createdAt).toLocaleString('ja-JP')}
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={() => handleDeleteComment(comment.commentId)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#dc3545',
                                cursor: 'pointer',
                                fontSize: '12px',
                                padding: '4px',
                                marginLeft: '8px',
                              }}
                            >
                              {t('sectionComments', 'delete')}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Comment Button / Form */}
                  {!showForm ? (
                    <button
                      onClick={() => { setShowForm(true); setFormType(activeTab === 'notes' ? 'note' : 'comment'); }}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px dashed #dee2e6',
                        borderRadius: '8px',
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                        color: '#007bff',
                        fontSize: '14px',
                        marginTop: '10px',
                      }}
                    >
                      + {t('sectionComments', 'addComment')}
                    </button>
                  ) : (
                    <div style={{
                      marginTop: '10px',
                      padding: '14px',
                      border: '1px solid #007bff',
                      borderRadius: '8px',
                      backgroundColor: '#f8f9ff',
                    }}>
                      {/* Text area */}
                      <textarea
                        value={formText}
                        onChange={(e) => setFormText(e.target.value)}
                        placeholder={t('sectionComments', 'textPlaceholder')}
                        style={{
                          width: '100%',
                          minHeight: '80px',
                          padding: '10px',
                          border: '1px solid #ced4da',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          resize: 'vertical',
                          boxSizing: 'border-box',
                        }}
                      />

                      {/* Options row */}
                      <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {/* Type */}
                        <select
                          value={formType}
                          onChange={(e) => setFormType(e.target.value as any)}
                          style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #ced4da', fontSize: '13px' }}
                        >
                          <option value="comment">{t('sectionComments', 'typeComment')}</option>
                          <option value="note">{t('sectionComments', 'typeNote')}</option>
                          <option value="suggestion">{t('sectionComments', 'typeSuggestion')}</option>
                        </select>

                        {/* Priority */}
                        <select
                          value={formPriority}
                          onChange={(e) => setFormPriority(e.target.value)}
                          style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #ced4da', fontSize: '13px' }}
                        >
                          <option value="">{t('sectionComments', 'priorityNone')}</option>
                          <option value="high">{t('sectionComments', 'priorityHigh')}</option>
                          <option value="medium">{t('sectionComments', 'priorityMedium')}</option>
                          <option value="low">{t('sectionComments', 'priorityLow')}</option>
                        </select>

                        {/* Image upload */}
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                          style={{
                            padding: '6px 10px',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            fontSize: '13px',
                          }}
                        >
                          {uploading ? t('sectionComments', 'uploading') : t('sectionComments', 'attachImage')}
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/png,image/jpeg,image/gif,image/webp"
                          style={{ display: 'none' }}
                          onChange={handleImageUpload}
                        />

                        {/* URL */}
                        <button
                          onClick={() => setShowUrlInput(!showUrlInput)}
                          style={{
                            padding: '6px 10px',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            fontSize: '13px',
                          }}
                        >
                          {t('sectionComments', 'addUrl')}
                        </button>
                      </div>

                      {/* Tags */}
                      <div style={{ marginTop: '8px' }}>
                        <input
                          value={formTags}
                          onChange={(e) => setFormTags(e.target.value)}
                          placeholder={t('sectionComments', 'tagsPlaceholder')}
                          style={{
                            width: '100%',
                            padding: '6px 10px',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            fontSize: '13px',
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>

                      {/* URL Input */}
                      {showUrlInput && (
                        <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center' }}>
                          <input
                            value={urlValue}
                            onChange={(e) => setUrlValue(e.target.value)}
                            placeholder={t('sectionComments', 'urlPlaceholder')}
                            style={{ flex: 2, padding: '6px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '13px' }}
                          />
                          <input
                            value={urlTitle}
                            onChange={(e) => setUrlTitle(e.target.value)}
                            placeholder={t('sectionComments', 'urlTitlePlaceholder')}
                            style={{ flex: 1, padding: '6px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '13px' }}
                          />
                          <button onClick={handleAddUrl} style={{
                            padding: '6px 10px', border: 'none', borderRadius: '4px',
                            backgroundColor: '#007bff', color: '#fff', cursor: 'pointer', fontSize: '13px',
                          }}>+</button>
                        </div>
                      )}

                      {/* Attachments preview */}
                      {formAttachments.length > 0 && (
                        <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {formAttachments.map((att, idx) => (
                            <div key={idx} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              backgroundColor: '#e9ecef',
                              borderRadius: '4px',
                              fontSize: '12px',
                            }}>
                              {att.type === 'image' ? '📷' : '🔗'} {att.name}
                              <button
                                onClick={() => removeAttachment(idx)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', fontSize: '14px', padding: '0 2px' }}
                              >&times;</button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Submit / Cancel */}
                      <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={resetForm}
                          style={{
                            padding: '8px 16px',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            fontSize: '13px',
                          }}
                        >
                          {t('sectionComments', 'cancel')}
                        </button>
                        <button
                          onClick={handleSubmitComment}
                          disabled={!formText.trim()}
                          style={{
                            padding: '8px 16px',
                            border: 'none',
                            borderRadius: '4px',
                            backgroundColor: formText.trim() ? '#007bff' : '#adb5bd',
                            color: '#fff',
                            cursor: formText.trim() ? 'pointer' : 'not-allowed',
                            fontSize: '13px',
                            fontWeight: 600,
                          }}
                        >
                          {t('sectionComments', 'submit')}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* AI Suggestions Tab */}
              {activeTab === 'ai' && (
                <>
                  <div style={{ marginBottom: '16px', display: 'flex', gap: '10px' }}>
                    <button
                      onClick={handleGenerateAi}
                      disabled={aiGenerating}
                      style={{
                        padding: '10px 18px',
                        border: 'none',
                        borderRadius: '6px',
                        backgroundColor: aiGenerating ? '#93c5fd' : '#007bff',
                        color: '#fff',
                        cursor: aiGenerating ? 'wait' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 600,
                      }}
                    >
                      {aiGenerating
                        ? t('sectionComments', 'aiGenerating')
                        : (data?.aiSuggestions?.length ? t('sectionComments', 'regenerateAi') : t('sectionComments', 'generateAi'))
                      }
                    </button>
                  </div>

                  {(!data?.aiSuggestions || data.aiSuggestions.length === 0) && !aiGenerating ? (
                    <div style={{ textAlign: 'center', padding: '30px', color: '#6c757d', fontSize: '14px' }}>
                      {t('sectionComments', 'noAiSuggestions')}
                    </div>
                  ) : (
                    <div>
                      {data?.aiSuggestions.map(suggestion => (
                        <div key={suggestion.suggestionId} style={{
                          padding: '12px',
                          marginBottom: '10px',
                          backgroundColor: suggestion.accepted ? '#d4edda' : '#f0f7ff',
                          borderRadius: '8px',
                          border: `1px solid ${suggestion.accepted ? '#c3e6cb' : '#b8daff'}`,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                <span style={{
                                  fontSize: '11px',
                                  padding: '2px 8px',
                                  borderRadius: '10px',
                                  backgroundColor: getSuggestionTypeColor(suggestion.type),
                                  color: '#fff',
                                  fontWeight: 600,
                                }}>
                                  {getSuggestionTypeLabel(suggestion.type)}
                                </span>
                                {suggestion.accepted && (
                                  <span style={{ fontSize: '11px', color: '#28a745', fontWeight: 600 }}>
                                    ✓ {t('sectionComments', 'accepted')}
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                                {suggestion.text}
                              </div>
                            </div>
                            {!suggestion.accepted && (
                              <button
                                onClick={() => handleAcceptSuggestion(suggestion)}
                                style={{
                                  padding: '6px 12px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: '#28a745',
                                  color: '#fff',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  marginLeft: '10px',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {t('sectionComments', 'acceptSuggestion')}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SectionCommentPopup;
